import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { feedbackService, type CreateFeedbackData } from "@/services/feedbacks";
import type { FeedbackCriterion } from "@/types/feedback";
import { auth, db, doc, getDoc } from "@/firebase";

export function useFeedbackForm() {
  const router = useRouter();
  const isSubmitting = ref(false);
  const error = ref<string | null>(null);
  const success = ref(false);

  // Get user type from Firebase
  const getUserType = async (uid: string) => {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.userType || "student";
      }
      return "student";
    } catch (error) {
      console.error("Error fetching user type:", error);
      return "student";
    }
  };

  const submitFeedback = async (
    sessionId: string,
    fromUserId: string,
    toUserId: string,
    criteria: FeedbackCriterion[],
    comment: string,
    subject: string
  ) => {
    if (isSubmitting.value) return;

    isSubmitting.value = true;
    error.value = null;
    success.value = false;

    try {
      // Calculate overall score from non-null ratings
      const validRatings = criteria.filter((criterion) => criterion.rating !== null);
      if (validRatings.length === 0) {
        throw new Error("No ratings provided");
      }

      const overallScore =
        validRatings.reduce((sum, criterion) => sum + (criterion.rating as number), 0) /
        validRatings.length;

      // Prepare feedback data
      const feedbackData: CreateFeedbackData = {
        session_id: sessionId,
        from_user_id: fromUserId,
        to_user_id: toUserId,
        overall_score: overallScore,
        comment: comment.trim() || undefined,
        subject: subject,
      };

      // Add individual criterion scores (only for non-null ratings)
      validRatings.forEach((criterion) => {
        const scoreKey = `${criterion.id}_score` as keyof CreateFeedbackData;
        (feedbackData as any)[scoreKey] = criterion.rating;
      });

      await feedbackService.createFeedback(feedbackData);
      success.value = true;

      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to submit feedback";
      console.error("Error submitting feedback:", err);
      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  const navigateBack = () => {
    router.back();
  };

  const navigateToSessions = async () => {
    // Determine where to navigate based on user type
    const currentUser = auth.currentUser;
    if (currentUser) {
      const userType = await getUserType(currentUser.uid);
      if (userType === "tutor") {
        router.push("/tutor/sessions");
      } else {
        router.push("/student/sessions");
      }
    } else {
      // Fallback to student sessions if no user
      router.push("/student/sessions");
    }
  };

  return {
    isSubmitting: computed(() => isSubmitting.value),
    error: computed(() => error.value),
    success: computed(() => success.value),
    submitFeedback,
    navigateBack,
    navigateToSessions,
  };
}

// Utility function to calculate rating label
export function getRatingLabel(rating: number): string {
  if (rating >= 4.5) return "Strongly Agree";
  if (rating >= 3.5) return "Agree";
  if (rating >= 2.5) return "Neutral";
  if (rating >= 1.5) return "Disagree";
  return "Strongly Disagree";
}

// Utility function to get rating color class
export function getRatingColorClass(rating: number): string {
  if (rating >= 4.5) return "text-green-600";
  if (rating >= 3.5) return "text-blue-600";
  if (rating >= 2.5) return "text-yellow-600";
  if (rating >= 1.5) return "text-orange-600";
  return "text-red-600";
}
